import React from "react";
import { Link } from "react-router-dom";
import { Printer } from "lucide-react";

const SECTIONS = [
    {
        h: "1. Who we are",
        p: [
            "Castline is a platform that helps brands license creator video content and turn it into reusable ad inventory across multiple advertising channels (the \"Service\"). \"Castline\", \"we\" and \"us\" refer to the team that builds and operates Castline.",
            "These Terms of Service govern your use of joincastline.com and any related applications, dashboards or APIs.",
        ],
    },
    {
        h: "2. Who can use Castline",
        p: [
            "You must be at least 18 years old, or the age of majority in your jurisdiction, to create an account.",
            "Three account roles exist: <strong>Creator</strong>, <strong>Brand</strong>, and <strong>Admin</strong>. Some features are role-restricted (for example, only Brands can fund payouts; only Creators can upload videos).",
            "You're responsible for the accuracy of the information you provide and for keeping your password secure.",
        ],
    },
    {
        h: "3. Creator content & licensing",
        p: [
            "When you (a Creator) upload a video to Castline, you keep ownership of the underlying content.",
            "By uploading and accepting an invite from a Brand, you grant that Brand a non-exclusive, worldwide license to use, modify, distribute and display the video as advertising creative across the channels they specify (e.g. Meta, Google, Amazon, YouTube), for the term defined in the commission you both agree to.",
            "You represent that you own or have the rights to the content you upload, that it doesn't infringe any third-party IP, and that any individuals visible in the content have agreed to its commercial use.",
            "Castline doesn't claim ownership of your videos. We only process them to deliver the Service.",
        ],
    },
    {
        h: "4. Payouts & rev-share",
        p: [
            "Brands fund creator payouts via Stripe. Castline holds funds briefly as a payment intermediary and forwards the agreed share to the Creator.",
            "During pilot periods, Castline may waive its platform fee at its sole discretion. After the pilot, a platform fee may be charged on each payout (current rate: 10% of the funded amount unless otherwise agreed in writing).",
            "Creators receive payouts via the destination they configure (Stripe, PayPal or bank transfer). Bank-side delays are outside our control.",
            "Refunds: if a Brand and Creator agree to cancel a commission before the video runs, the Brand may request a refund. After the video has been published or run as an ad, payouts are non-refundable.",
        ],
    },
    {
        h: "5. Acceptable use",
        p: [
            "You agree not to: (a) upload illegal content; (b) infringe IP rights; (c) misrepresent your identity; (d) reverse-engineer, scrape or interfere with the Service; (e) use the Service to send spam.",
            "Castline may suspend or terminate accounts that violate these rules, with or without notice.",
        ],
    },
    {
        h: "6. Third-party platforms",
        p: [
            "When you connect Castline to third parties (Meta, Google, Amazon, Stripe, etc.), you agree to their respective terms.",
            "Castline isn't responsible for outages, policy changes or data restrictions imposed by these platforms.",
        ],
    },
    {
        h: "7. Service availability",
        p: [
            "We aim for high availability but don't guarantee uninterrupted Service. Castline is provided \"as is\" without warranty of any kind, to the extent permitted by law.",
        ],
    },
    {
        h: "8. Limitation of liability",
        p: [
            "To the maximum extent allowed by law, Castline's aggregate liability arising out of or relating to these Terms is limited to the greater of (i) the amounts paid by you to Castline in the past 12 months, or (ii) US $100.",
            "Castline isn't liable for indirect, incidental or consequential damages, including lost revenue, lost data, or lost business opportunities.",
        ],
    },
    {
        h: "9. Termination",
        p: [
            "You can delete your account at any time by emailing <a href=\"mailto:hello@joincastline.com\">hello@joincastline.com</a>.",
            "We may suspend or terminate your account if we believe you've materially breached these Terms.",
            "On termination, your data is deleted within 30 days, except where we're legally required to retain it.",
        ],
    },
    {
        h: "10. Changes",
        p: [
            "We may update these Terms occasionally. If changes are material, we'll notify active users by email at least 14 days before they take effect. Continued use of the Service after the effective date constitutes acceptance.",
        ],
    },
    {
        h: "11. Governing law",
        p: [
            "These Terms are governed by applicable consumer-protection and contract law in the jurisdictions in which we operate. Disputes will first be addressed informally by emailing <a href=\"mailto:hello@joincastline.com\">hello@joincastline.com</a>. If we can't resolve them informally within 30 days, either party may pursue them in a court of competent jurisdiction.",
        ],
    },
    {
        h: "12. Contact",
        p: [
            "Questions about these Terms? Reach us at <a href=\"mailto:hello@joincastline.com\">hello@joincastline.com</a>.",
        ],
    },
];

export default function Terms() {
    return (
        <div className="min-h-screen bg-white text-slate-900 legal-page">
            <header className="border-b hairline bg-white sticky top-0 z-40 print:hidden">
                <div className="max-w-[760px] mx-auto px-5 lg:px-8 h-16 flex items-center justify-between">
                    <Link to="/" className="flex items-center gap-2">
                        <div className="w-8 h-8 rounded-full bg-coral flex items-center justify-center text-white font-display font-bold text-sm">c</div>
                        <span className="font-display text-lg font-semibold">castline</span>
                        <span className="text-slate-300 mx-1">·</span>
                        <span className="text-xs text-slate-500 uppercase tracking-wider">Terms of Service</span>
                    </Link>
                    <button onClick={() => window.print()} className="btn-pill-ghost inline-flex items-center text-sm">
                        <Printer className="w-4 h-4 mr-2" /> PDF
                    </button>
                </div>
            </header>
            <main className="max-w-[760px] mx-auto px-5 lg:px-8 py-12 lg:py-16">
                <div className="chip chip-coral inline-flex mb-4">VERSION 1 · APRIL 2026</div>
                <h1 className="font-display text-4xl md:text-5xl font-semibold mb-3 leading-tight tracking-tight">
                    Terms of Service
                </h1>
                <p className="text-sm text-slate-500 mb-10">
                    Effective date: 28 April 2026. By using Castline you agree to these Terms.
                </p>
                <div className="space-y-8">
                    {SECTIONS.map((s) => (
                        <section key={s.h} data-testid={`tos-${s.h.split(".")[0]}`}>
                            <h2 className="font-display text-xl font-semibold mb-3 text-slate-900">{s.h}</h2>
                            {s.p.map((para, i) => (
                                <p
                                    key={i}
                                    className="text-sm text-slate-700 leading-relaxed mb-3"
                                    dangerouslySetInnerHTML={{ __html: para }}
                                />
                            ))}
                        </section>
                    ))}
                </div>
                <footer className="border-t hairline mt-14 pt-6 text-xs text-slate-400 flex flex-wrap justify-between gap-3">
                    <div>Castline · joincastline.com</div>
                    <div>
                        <Link to="/privacy" className="hover:text-coral">Privacy</Link>
                        <span className="text-slate-300 mx-2">·</span>
                        <a href="mailto:hello@joincastline.com" className="hover:text-coral">hello@joincastline.com</a>
                    </div>
                </footer>
            </main>
        </div>
    );
}
